import React from 'react';
import { Form } from 'react-bootstrap';
import { useState } from 'react';
import { Card } from 'react-bootstrap-v5';
import 'react-toastify/dist/ReactToastify.css';
import { numberFormat } from '../helpers/GlobalHelper';
import DataTable from './DataTable';
import { useLocation } from "react-router-dom";

const ItemRateHistoryReport: React.FC = () => {
    const location = useLocation();
    const query = new URLSearchParams(location.search);

    const [formData, setFormData] = useState({
        fromDate: query.get('startDate') ?? '',
        toDate: query.get('endDate') ?? '',
        idProducts: query.get('idProducts') ?? '',
    });

    const [filterColumns, setFilterColumns] = useState({
        fromDate: query.get('startDate') ?? '',
        toDate: query.get('endDate') ?? '',
        idProducts: query.get('idProducts') ?? '',
        order: 'DESC',
        sortedColumn: 'id',
    });

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData({ ...formData, [name]: value });
        setFilterColumns({ ...filterColumns, [name]: value });
    };

    const columns = [
        { value: 'id', title: 'SN', style: { textAlign: 'center' } },
        { value: 'changeDate', title: 'Change Date', style: { textAlign: 'left' } },
        { value: 'changedBy', title: 'Changed By', style: { textAlign: 'left' } },
        { value: 'reason', title: 'Reason', style: { textAlign: 'left' } },
        { value: 'productName', title: 'Product Name', style: { textAlign: 'left' } },
        {
            value: 'oldProductionCost', title: 'Old Production Cost', style: { textAlign: 'center' },
            hasHtmlComponent: true,
            htmlComponent: (row: any) => {
                return (
                    <>
                        {numberFormat(row.oldProductionCost)}
                    </>
                )
            },
        },
        {
            value: 'newProductionCost', title: 'New Production Cost', style: { textAlign: 'center' },
            hasHtmlComponent: true,
            htmlComponent: (row: any) => {
                return (
                    <>
                        {numberFormat(row.newProductionCost)}
                    </>
                )
            },
        },
        {
            value: 'proChangePercent', title: 'Pro. Change (%)', style: { textAlign: 'center' },
            hasHtmlComponent: true,
            htmlComponent: (row: any) => {
                return (
                    <>
                        {numberFormat(row.proChangePercent)}%
                    </>
                )
            },
        },
        { value: 'item', title: 'Item', style: { textAlign: 'left' } },
        {
            value: 'oldRate', title: 'Old Rate', style: { textAlign: 'center' },
            hasHtmlComponent: true,
            htmlComponent: (row: any) => {
                return (
                    <>
                        {numberFormat(row.oldRate)}
                    </>
                )
            },
        },
        {
            value: 'newRate', title: 'New Rate', style: { textAlign: 'center' },
            hasHtmlComponent: true,
            htmlComponent: (row: any) => {
                return (
                    <>
                        {numberFormat(row.newRate)}
                    </>
                )
            },
        },
        {
            value: 'changePercent', title: 'Change %', style: { textAlign: 'center' },
            hasHtmlComponent: true,
            htmlComponent: (row: any) => {
                return (
                    <>
                        {numberFormat(row.changePercent)}%
                    </>
                )
            },
        },
    ]

    const filters = {
        id: {
            filterType: 'like',
            filterValue: '',
        },
        changeDate: {
            filterType: 'like',
            filterValue: '',
        },
        changedBy: {
            filterType: 'like',
            filterValue: '',
        },
        reason: {
            filterType: 'like',
            filterValue: '',
        },
        productName: {
            filterType: 'like',
            filterValue: '',
        },
        oldProductionCost: {
            filterType: 'like',
            filterValue: '',
        },
        newProductionCost: {
            filterType: 'like',
            filterValue: '',
        },
        proChangePercent: {
            filterType: 'like',
            filterValue: '',
        },
        item: {
            filterType: 'like',
            filterValue: '',
        },
        oldRate: {
            filterType: 'like',
            filterValue: '',
        },
        newRate: {
            filterType: 'like',
            filterValue: '',
        },
        changePercent: {
            filterType: 'like',
            filterValue: '',
        },
    }

    const url = process.env.REACT_APP_API_URL + 'reports/product-global-rate-history'

    const additionalPostData = {
        fromDate: formData.fromDate,
        toDate: formData.toDate,
        idProducts: formData.idProducts,
    };

    const getFilterParams = (data: any) => {
        // Create an updated filterColumns object
        const updatedColumns: Record<string, any> = {};

        // Iterate through each column in the data.columns object
        Object.keys(data.columns).forEach((columnKey: string) => {
            const column = data.columns[columnKey];
            updatedColumns[columnKey] = column.filterValue || ''; // Default to empty string if filterValue is undefined
        });

        setFilterColumns({
            ...filterColumns, // Include the existing column filters
            ...updatedColumns, // Include the new column filters
            order: data.order || 'DESC',
            sortedColumn: data.sorted_column || 'id',
            globalSearch: data.globalSearch || '',
            token: data.token || '',
        });
    }

    return (
        <>
            <Card className='mb-4'>
                <Card.Body>
                    <Card.Text>
                        <div className='row'>
                            <div className='col-md-3'>
                                <Form.Group className="mb-3">
                                    <Form.Label>From Date<span className='required'></span></Form.Label>
                                    <Form.Control 
                                        type="date" 
                                        placeholder="From Date" 
                                        name='fromDate' 
                                        value={formData.fromDate}
                                        onChange={handleChange} 
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-3'>
                                <Form.Group className="mb-3">
                                    <Form.Label>To Date<span className='required'></span></Form.Label>
                                    <Form.Control 
                                        type="date" 
                                        placeholder="To Date" 
                                        name='toDate' 
                                        value={formData.toDate}
                                        onChange={handleChange} 
                                    />
                                </Form.Group>
                            </div>
                        </div>
                    </Card.Text>
                </Card.Body>
            </Card>

            <div className='row'>
                <div className='col-12'>
                    <DataTable 
                        url={url} 
                        columns={columns} 
                        filters={filters} 
                        additionalPostData={additionalPostData} 
                        onValueChange={(state: any) => {
                            getFilterParams(state);
                        }}
                    />
                </div>
            </div>
        </>
    );
};

export default ItemRateHistoryReport;
